import BorderColorRoundedIcon from '@mui/icons-material/BorderColorRounded';
import {
  Avatar,
  Box,
  Button,
  Container,
  Stack,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { PublicSignerSessionResponse } from '../publicSignerTypes';
import {
  getDisclosureFacilityName,
  getDisclosureRequestTitle,
  getDisclosureSignerLabel
} from '../utils/presentation';

export function PublicSignerDisclosureGate({
  session,
  onContinue
}: {
  session: PublicSignerSessionResponse;
  onContinue: () => void;
}) {
  const requestTitle = getDisclosureRequestTitle(session);
  const facilityName = getDisclosureFacilityName(session);
  const signerLabel = getDisclosureSignerLabel(session);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 8, md: 14 } }}>
      <Stack
        alignItems="center"
        spacing={3}
        sx={(theme) => ({
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '16px',
          boxShadow: `0px 5px 10px ${alpha(theme.palette.secondary.main, 0.08)}`,
          px: { xs: 3, sm: 5 },
          py: { xs: 5, sm: 6 },
          textAlign: 'center'
        })}
      >
        <Avatar
          sx={(theme) => ({
            backgroundColor: theme.palette.info.main,
            color: 'primary.main',
            height: 76,
            width: 76
          })}
        >
          <BorderColorRoundedIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Stack spacing={1}>
          <Typography color="secondary.main" fontWeight={600} variant="h6">
            Electronic Signature Request
          </Typography>
          <Typography color="text.primary" variant="body1">
            You have been requested to sign{' '}
            <Box component="span" sx={{ fontWeight: 500 }}>
              {requestTitle}
            </Box>{' '}
            {facilityName ? (
              <>
                for{' '}
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {facilityName}
                </Box>{' '}
              </>
            ) : null}
            electronically.
          </Typography>
          {signerLabel ? (
            <Typography color="text.primary" variant="body1">
              {signerLabel}
            </Typography>
          ) : null}
        </Stack>
        <Stack spacing={1.25} sx={{ maxWidth: 560 }}>
          <Typography color="secondary.main" fontWeight={600} variant="h6">
            Electronic signature consent
          </Typography>
          <Typography color="#364955" variant="body2">
            By selecting <strong>Start Signing</strong>, you agree to use
            electronic records and signatures and confirm that you intend to
            sign this document electronically. You acknowledge that your
            electronic signature will be legally binding and have the same
            effect as a handwritten signature.
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="center">
          <Button color="primary" onClick={onContinue} variant="contained">
            Start Signing
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
